import * as React from 'react';

import { createCache, createFastCacheKeyWithCollisionRisk } from '@gitbook/util-suspense-cache';
import { assertNever } from '@gitbook/util-ts';

import { HeadEnabledContext } from './contexts';
import type {
    HeadEntry,
    HeadEntryLink,
    HeadEntryMeta,
    HeadEntryScript,
    HeadEntryStyle,
} from './types';

interface HeadController {
    setTitle: (title: string, suffixx?: string) => void;

    /**
     * Write a head entry and suspends rendering while loading the potential associated resource.
     * It also throws an error if loading fails.
     */
    write: (entries: HeadEntry | HeadEntry[]) => void;

    /**
     * Write a head entry and suspends rendering while loading the potential associated resource.
     * It doesn't throw an error if loading fails.
     */
    writeSafe: (entries: HeadEntry | HeadEntry[]) => void;

    /**
     * Write a head entry without suspending rendering during loading.
     * The entry will still be registered during SSR and inserted in the generated HTML.
     */
    writeAsync: (entries: HeadEntry | HeadEntry[]) => Promise<void>;

    /**
     * Set the language value for the whole document. The lang attribute is attached to the <html> tag.
     */
    setLang: (lang: string) => void;

    /**
     * Set the theme (dark/light mode, radius, etc) css classes on the <body>.
     */
    setTheme: (theme: string) => void;

    /**
     * Set the styles of the page. It will bypass the cache if needed.
     */
    setStyles: (entry: HeadEntryStyle, ignoreCache: boolean) => void;
}

/**
 * Hook to access the controller to edit the web page head.
 */
export function useHead(
    run: (head: Pick<HeadController, 'write' | 'writeAsync' | 'setStyles'>) => void
) {
    const enabled = React.useContext(HeadEnabledContext);

    if (enabled) {
        run(head);
    }
}

interface HeadTitleProps {
    /**
     * Title of the page.
     */
    title: string;
    /**
     * Suffix of the title.
     * @default "GitBook"
     */
    suffix?: string;
}

let titleStack: { id: string; title: string }[] = [];

/**
 * Hook to set the title of the page.
 * It will set the title to "title - suffix".
 * @param title Title of the page.
 * @param suffix Suffix of the title.
 * @default "GitBook"
 */
export function useHeadTitle(title: string, suffix = 'GitBook') {
    const enabled = React.useContext(HeadEnabledContext);
    const id = React.useId();
    React.useLayoutEffect(() => {
        if (enabled) {
            const computedTitle = `${title} - ${suffix}`;
            window.document.title = computedTitle;
            titleStack.unshift({ id, title: computedTitle });
            return () => {
                // If the title is not the last one, we don't restore it
                if (titleStack[0].id !== id) {
                    titleStack = titleStack.filter((t) => t.id !== id);
                    return;
                }
                // Pop the title from the stack
                titleStack.shift();
                // Set the title to the previous one
                window.document.title = titleStack[0]?.title || suffix;
            };
        }
    });
}

/**
 * Component to set the title of the page.
 * It will set the title to "title - suffix".
 */
export function HeadTitle(props: HeadTitleProps) {
    useHeadTitle(props.title, props.suffix);
    return null;
}

/**
 * Interface to edit the meta informations of the page (title, stylesheets, scripts).
 * It handles SSR.
 */
export const head = {
    setStyles: (entry: HeadEntryStyle, ignoreCache: boolean) => {
        if (ignoreCache) {
            head.cache.clear(entry);
        }

        head.writeAsync(entry);
    },

    /**
     * Write an entry in the document <head>. This method is optimized to cache unique entries.
     * This method can be used both on client and server side.
     */
    write: (entries: HeadEntry | HeadEntry[]) => {
        head.cache.readMultiple(Array.isArray(entries) ? entries : [entries]);
    },

    /**
     * Write an entry in the document <head>. Without crashing if the script fails to load.
     */
    writeSafe: (entries: HeadEntry | HeadEntry[]) => {
        head.cache.readMultipleSafe(Array.isArray(entries) ? entries : [entries]);
    },

    /**
     * Write an entry in the document <head> without waiting for it to be loaded. This method is optimized to cache unique entries.
     * This method can be used both on client and server side.
     */
    writeAsync: async (entries: HeadEntry | HeadEntry[]) => {
        head.cache
            .readMultipleAsync(Array.isArray(entries) ? entries : [entries])
            // Callers cannot observe the loading here, and third-party resources are
            // routinely blocked by adblockers or proxies: don't leave an unhandled rejection.
            .catch(() => {});
    },

    /**
     * Suspense cache to load head entries when needed.
     */
    cache: createCache<HeadEntry, HeadEntry>({
        getKey: (entry) => `${entry.type}_${createFastCacheKeyWithCollisionRisk(entry)}`,
        getValue: (entry) => {
            if (typeof window === 'undefined') {
                // during server-side rendering, nothing is done!
                return entry;
            }

            switch (entry.type) {
                case 'link':
                    return insertLink(entry);
                case 'style':
                    return insertStyle(entry);
                case 'script':
                    return insertScript(entry);
                case 'meta':
                    return insertMeta(entry);
                default:
                    assertNever(entry);
            }
        },
    }),

    /**
     * Reset the head state.
     */
    reset: () => {
        head.cache.clearAll();
    },
} as const;

/**
 * Insert a link (CSS stylesheet or icon) in the document and resolve when the link is loaded.
 */
function insertLink(entry: HeadEntryLink): Promise<HeadEntryLink> {
    return new Promise((resolve, reject) => {
        const uniqueKey = getHeadUniqueKey(entry);
        if (uniqueKey) {
            const existingLink = document.getElementById(uniqueKey) as HTMLLinkElement;
            if (existingLink) {
                existingLink.href = entry.href;
                resolve(entry);
                return;
            }
        }

        if (entry.rel === 'preload') {
            // We don't insert preload links when the document has already been loaded.
            return;
        }

        const head = document.getElementsByTagName('head')[0];
        const link = document.createElement('link');
        link.rel = entry.rel;
        link.href = entry.href;

        if (entry.rel === 'stylesheet') {
            link.type = 'text/css';
            link.media = 'all';
        }

        if (entry.crossOrigin) {
            link.crossOrigin = entry.crossOrigin;
        }

        if (uniqueKey) {
            link.id = uniqueKey;
        }

        link.onload = () => {
            resolve(entry);
        };

        link.onerror = function () {
            reject(new Error(`Failed to load ${this.href}`));
        };

        head.appendChild(link);
    });
}

/**
 * Insert a Script in the document and resolve when loaded.
 */
function insertScript(entry: HeadEntryScript): Promise<HeadEntryScript> {
    return new Promise((resolve, reject) => {
        const head = document.getElementsByTagName('head')[0];
        const script = document.createElement('script');
        script.type = 'text/javascript';
        if (entry.defer) {
            script.defer = true;
        } else {
            script.async = true;
        }
        script.src = entry.src;

        if (entry.id) {
            script.id = entry.id;
        }

        if (entry.dataset) {
            Object.entries(entry.dataset).forEach(([attrName, attrValue]) => {
                script.setAttribute(`data-${attrName}`, attrValue);
            });
        }

        script.onload = () => {
            if (entry.onLoad) {
                entry.onLoad();
            }
            resolve(entry);
        };

        script.onerror = function () {
            reject(new Error(`Failed to load ${this.src}`));
        };

        head.appendChild(script);
    });
}

/**
 * Insert a Style in the document and resolve when loaded.
 */
function insertStyle(entry: HeadEntryStyle): Promise<HeadEntryStyle> {
    return new Promise((resolve, reject) => {
        const uniqueKey = getHeadUniqueKey(entry);

        if (uniqueKey) {
            const existingStyle = document.getElementById(uniqueKey) as
                | HTMLStyleElement
                | undefined;
            if (existingStyle) {
                existingStyle.innerHTML = entry.content;
                resolve(entry);
                return;
            }
        }

        const head = document.getElementsByTagName('head')[0];
        const style = document.createElement('style');
        style.innerHTML = entry.content;

        if (uniqueKey) {
            style.id = uniqueKey;
        }

        style.onload = () => {
            resolve(entry);
        };

        style.onerror = () => {
            reject(new Error('Failed to load style'));
        };

        head.appendChild(style);
    });
}

/**
 * Insert a meta in the document.
 */
function insertMeta(entry: HeadEntryMeta): Promise<HeadEntryMeta> {
    return new Promise((resolve) => {
        const uniqueKey = getHeadUniqueKey(entry);
        if (uniqueKey) {
            const existingMeta = document.getElementById(uniqueKey) as HTMLMetaElement;
            if (existingMeta) {
                existingMeta.content = entry.content;
                resolve(entry);
                return;
            }
        }

        const head = document.getElementsByTagName('head')[0];
        const meta = document.createElement('meta');
        meta.name = entry.name;
        meta.content = entry.content;

        if (uniqueKey) {
            meta.id = uniqueKey;
        }

        head.appendChild(meta);
        resolve(entry);
    });
}

/**
 * Return a unique key for the given entry.
 */
function getHeadUniqueKey(entry: HeadEntryMeta): string;
function getHeadUniqueKey(entry: HeadEntry): string | undefined;
function getHeadUniqueKey(entry: HeadEntry): string | undefined {
    switch (entry.type) {
        // we want to replace meta tags with the same name
        case 'meta':
            return `__meta-${entry.name}`;
        case 'link': {
            if (entry.rel === 'icon') {
                // there can be only one icon
                return `__link-${entry.rel}`;
            }
            break;
        }
        case 'style':
            return entry.id ? `__style-${entry.id}` : undefined;
        default:
            return undefined;
    }
}
